import { pipe } from 'fp-ts/lib/function';
import { failure, parse, string, success } from 'io-ts/lib/Decoder';

import { isUUID } from '../typescript/uuid';

export const UUID = pipe(
	string,
	parse((s) => {
		if (isUUID(String(s))) {
			return success(s);
		}

		return failure(s, 'a valid UUID');
	})
);

/**
 * The characters allowed after the `:` of an id whose decoder opts into
 * `allowLegacyIds`, when that part is not a UUID: sentinels such as
 * `user:system` and legacy Auth0 subjects such as `user:auth0|<hex>`.
 *
 * Quotes, whitespace, `:` and `;` are deliberately excluded, so a decoded id can
 * never carry trailing or injected content.
 */
const reLegacyIdSuffix = /^[a-z0-9._@|-]+$/;

type WithPrefixOptions = {
	/**
	 * Additionally accepts a non-UUID suffix made only of
	 * {@link reLegacyIdSuffix} characters. It exists for the entities whose
	 * stored ids predate UUIDs; never enable it for a new entity.
	 */
	allowLegacyIds?: boolean;
	/**
	 * Additionally accepts `<prefix>:<uuid>:<uuid>`, the id of a child record
	 * colocated under its parent (e.g. an FPC sub-job, `fpc-job:<uuid>:<uuid>`).
	 * Both segments must be UUIDs.
	 */
	allowSubId?: boolean;
};

/**
 * Decodes an entity id of the exact form `<prefix>:<uuid>`. Anything after the
 * uuid — a second `:` segment, whitespace, SQL — fails, because ids decoded here
 * are trusted downstream (DynamoDB keys, report SQL parameters).
 *
 * See {@link WithPrefixOptions} for the opt-in exceptions.
 */
export const withPrefix = (
	prefix: string,
	{ allowLegacyIds = false, allowSubId = false }: WithPrefixOptions = {}
) =>
	pipe(
		string,
		parse((s) => success(s.toLowerCase())),
		parse((s) => {
			const expectedPrefix = `${prefix}:`;

			if (!s.startsWith(expectedPrefix)) {
				return failure(s, `a valid ${prefix} id`);
			}

			const suffix = s.slice(expectedPrefix.length);

			if (isUUID(suffix)) {
				return success(s);
			}

			if (allowSubId) {
				const segments = suffix.split(':');

				if (segments.length === 2 && segments.every((segment) => isUUID(segment))) {
					return success(s);
				}
			}

			if (allowLegacyIds && reLegacyIdSuffix.test(suffix)) {
				return success(s);
			}

			return failure(s, 'a valid UUID');
		})
	);
